import { useEffect, useMemo } from 'react';

import { createFieldPolicy, FieldPolicyConfig } from './createFieldPolicy';
import { SelectorOn } from './selector';
import { FieldDef, FormValuesOf } from './types';
import { useChangedFieldsTracking } from './useChangedFieldsTracking';
import { useFormWithNameInputProp } from './useFormWithNameInputProp';

/**
 * A policy, its resolution context and its form, from one call.
 *
 * Returns `{ policy, ctx, form }` rather than a component on purpose: a component identity minted
 * per render is a new element *type*, which remounts the subtree - with a live form that means
 * inputs losing focus on every keystroke. The caller passes `policy`/`ctx`/`form` straight onto
 * `<PolicyScope>`.
 *
 * `ctx` is a plain function, NOT a hook. Building a context usually needs app settings, and that
 * module reads `window.location` when it loads; calling it here would drag it into the framework
 * barrel, which the pure policy test suites load. The caller closes over whatever it needs.
 */

/**
 * The type of `form` in `useFormPolicy`'s return, for a given values shape - the public name for
 * "whatever `useFormWithNameInputProp` plus `useChangedFieldsTracking` add up to". A feature typing
 * its own captured form (e.g. `FinalizeForm`) uses this instead of reaching into either of those
 * directly, so it never needs to know they exist, let alone follow them if their wiring changes.
 *
 * Named `PolicyFormInstance`, not `PolicyForm` - `createPolicyField.tsx` already exports a
 * `PolicyForm` (the minimal structural slice a widget needs); this one is the full, concrete
 * instance a feature actually gets back.
 */
export type PolicyFormInstance<TValues> = ReturnType<typeof useChangedFieldsTracking<ReturnType<typeof useFormWithNameInputProp<TValues>>>>;

/**
 * `FieldPolicyConfig`'s own `readOnly` is omitted deliberately: there it means "where the disabled
 * state comes from" `(ctx) => boolean`, here it is the boolean handed to the context builder. Two
 * meanings, one name - so only the latter is offered, and the resolver falls back to `ctx.readOnly`.
 */
interface FormPolicyOptions<TCtx extends object, TFields extends Record<string, FieldDef<SelectorOn<any>, any>>, TValues = unknown> extends Omit<
  FieldPolicyConfig<TCtx, TFields, TValues>,
  'readOnly'
> {
  /** Seeds the form, and re-seeds it when it changes. */
  entity: unknown;
  readOnly?: boolean;
  /** Builds the resolution context. Memoised on `[entity, readOnly]` - see the note below. */
  ctx: (entity: unknown, readOnly: boolean) => TCtx;
  /**
   * When true (default), the returned form exposes `getChangedValues()` - the top-level fields
   * that differ from the last seed/reseed, for a caller that wants to submit only what changed.
   * Set false to make `getChangedValues()` return the full `form.values` instead (no filtering).
   */
  trackChangedFields?: boolean;
}

export const useFormPolicy = <TCtx extends object, TFields extends Record<string, FieldDef<SelectorOn<any>, any>>, TValues = unknown>({
  entity,
  readOnly = false,
  ctx: buildCtx,
  trackChangedFields = true,
  ...config
}: FormPolicyOptions<TCtx, TFields, TValues>) => {
  // Memoised on the policy inputs only. Anything else the builder closes over - country, forwarder
  // flag - is fixed for the life of the app, so it cannot go stale. A context axis that CAN change
  // at runtime would need to arrive through `entity` instead.
  const policy = createFieldPolicy<TCtx, TFields, TValues>(config as FieldPolicyConfig<TCtx, TFields, TValues>);
  const ctx = useMemo(() => buildCtx(entity, readOnly), [entity, readOnly]);

  // Memoised on [entity, ctx]: unmemoised, this recomputed (and re-ran every builder in the
  // registry, several of which derive a subtree from the entity) on every render of the owning
  // component, for any reason at all - wasteful at best, and actively wrong for a builder that
  // isn't perfectly pure about the entity it's given.
  const initialValues = useMemo(() => policy.buildInitialValues(entity, ctx) as FormValuesOf<TFields>, [entity, ctx]);

  // The form's shape comes from the registry's own paths - no hand-written value interface to drift.
  // Consumers re-declare what they need at the scope boundary (`usePolicyScope`'s extra type).
  const form = useFormWithNameInputProp<FormValuesOf<TFields>>({
    initialValues,
    /**
     * DISPLAY ONLY. Errors surface through each field's `error` prop on blur; submit does not
     * consult them, so nothing that saves today can start failing.
     *
     * A fresh closure each render, so it always sees the current ctx: Mantine memoises its validate
     * callback on the rules identity, which changes every render.
     */
    validate: (values) => policy.buildErrors(values, ctx),
    validateInputOnBlur: true,
  });

  // Adds a `setValues` that moves the changed-fields baseline forward on every call (this hook's
  // own reseed effect below, or a feature calling `form.setValues` directly), plus
  // `getChangedValues()` - see `useChangedFieldsTracking` for the exact semantics.
  const trackedForm = useChangedFieldsTracking(form, initialValues, trackChangedFields);

  /**
   * Re-seeds on the entity only, NOT on ctx: ctx is memoised on `[entity, readOnly]`, and re-seeding
   * when `readOnly` flips would discard whatever the user had typed.
   *
   * Safe with the visibility filter above because `present` is selected on axes the entity carries
   * (group, txClass, flow) or on fixed app settings (country, isForwarder) - `readOnly` and
   * `isAmendment` are unrepresentable in a selector. So a field cannot become present without the
   * entity changing, which re-seeds.
   */
  useEffect(() => {
    trackedForm.setValues(policy.buildInitialValues(entity, ctx) as FormValuesOf<TFields>);
  }, [entity]);

  return { policy, ctx, form: trackedForm };
};
